import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import type { EnvironmentDetailResponse } from "@starsach/shared-types";
import { useCustomer } from "../../api/hooks";
import { EnvironmentGate, EnvironmentPicker, useRememberEnvironment } from "../../components/Environment";
import { ErrorState, LoadingState, PageHeader } from "../../components/ui";
import { moduleById, type ModuleId } from "../../lib/modules";

/**
 * One environment-scoped module page: the environment comes from the
 * path (`/quotas/:environmentId`); with none, the gate picks or asks. The
 * header names the module and offers the environment picker; the body
 * gets the environment's detail (name, enabled components).
 */
export function ModulePage({
  module: id,
  section,
  title,
  subtitle,
  actions,
  children,
}: {
  module: ModuleId;
  section?: string;
  title?: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: (detail: EnvironmentDetailResponse, environmentId: string) => ReactNode;
}) {
  const module = moduleById(id);
  const { environmentId } = useParams();
  useRememberEnvironment(environmentId);
  const q = useCustomer(environmentId ?? "");
  if (!environmentId) return <EnvironmentGate module={module} section={section} />;
  return (
    <>
      <PageHeader
        title={title ?? module.label}
        subtitle={subtitle ?? (q.data ? <><Link to={`/customers/${environmentId}`} className="underline">{q.data.customer.friendly_name}</Link> · AWS account {q.data.customer.aws_account_number}</> : undefined)}
        actions={
          <>
            {actions}
            <EnvironmentPicker module={module} environmentId={environmentId} section={section} />
          </>
        }
      />
      {q.isPending && <LoadingState label="Loading environment" />}
      {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}
      {q.data && children(q.data, environmentId)}
    </>
  );
}

/** A section the mock-ups name and the product has not built yet: say what it will be, never a dead link. */
export function PlannedSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-dashed border-neutral-300 p-6 text-sm text-neutral-700 dark:border-neutral-700 dark:text-neutral-300">
      <h2 className="mb-2 text-base font-semibold text-ink dark:text-white">{title}</h2>
      {children}
    </section>
  );
}
